/**
 * LevelBuilder - Constructs static level elements
 * 
 * Handles creation of platforms, stars, puzzles, goals, and decorations.
 * Separates level construction logic from GameScene.
 * 
 * @module systems/LevelBuilder
 */

import Phaser from 'phaser';
import { PHYSICS } from '../configs/GameConfig.js';
import eventBus, { GameEvents } from '../core/EventBus.js';

export default class LevelBuilder {
    /**
     * @param {Phaser.Scene} scene - The game scene
     */
    constructor(scene) {
        this.scene = scene;
        this.trackedBodies = [];
        this.stars = [];
        
        // Puzzle elements
        this.door = null;
        this.doorGraphic = null;
        this.button = null;
        this.box = null;
        this.magneticBox = null;
        this.magneticBoxOriginalPosition = null;
        this.floatingBoxZone = null;
        
        // Goal
        this.goal = null;
        this.goalText = null;
        
        // Moving platforms
        this.movingPlatforms = [];
    }

    /**
     * Build all level elements
     * @param {Object} config - Level configuration
     */
    build(config) {
        this.createGradientBackground(config);
        this.createPlatforms(config);
        this.createMovingPlatforms(config);
        this.createStars(config);
        this.createPuzzle(config);
        this.createGoal(config);
        this.addDecorations(config);
        this.createDangerZone(config);
    }

    /**
     * Create gradient background
     * @param {Object} config - Level configuration
     */
    createGradientBackground(config) {
        const graphics = this.scene.add.graphics();
        const width = config.worldWidth;
        const height = config.worldHeight;
        const colors = config.theme.bgGradient;

        const parseColor = (hex) => {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : { r: 0, g: 0, b: 0 };
        };

        const color1 = parseColor(colors[0]);
        const color2 = parseColor(colors[1]);

        const steps = 30;
        for (let i = 0; i < steps; i++) {
            const t = i / steps;
            const r = Math.floor(color1.r + t * (color2.r - color1.r));
            const g = Math.floor(color1.g + t * (color2.g - color1.g));
            const b = Math.floor(color1.b + t * (color2.b - color1.b));
            const color = (r << 16) | (g << 8) | b;

            graphics.fillStyle(color, 1);
            graphics.fillRect(0, (height / steps) * i, width, height / steps + 1);
        }
        graphics.setScrollFactor(0.3);
    }

    /**
     * Create static platforms
     * @param {Object} config - Level configuration
     */
    createPlatforms(config) {
        config.platforms.forEach(p => {
            this.addPlatform(p.x, p.y, p.w, p.h, config.theme.platformColor);
        });
    }

    /**
     * Add a single platform
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {number} w - Width
     * @param {number} h - Height
     * @param {number} color - Platform color
     */
    addPlatform(x, y, w, h, color) {
        this.scene.matter.add.rectangle(x + w / 2, y + h / 2, w, h, { 
            isStatic: true, 
            friction: PHYSICS.PLATFORM.FRICTION,
            frictionStatic: PHYSICS.PLATFORM.FRICTION_STATIC,
            restitution: PHYSICS.PLATFORM.RESTITUTION,
            chamfer: { radius: PHYSICS.PLATFORM.CHAMFER_RADIUS },
            slop: PHYSICS.PLATFORM.SLOP
        });

        const platform = this.scene.add.graphics();
        platform.fillStyle(color, 1);
        platform.fillRect(x, y, w, h);

        // Highlights
        platform.fillStyle(0xffffff, 0.25);
        platform.fillRect(x, y, w, 5);
        platform.fillStyle(0x000000, 0.35);
        platform.fillRect(x, y + h - 5, w, 5);
        platform.fillStyle(0xffffff, 0.1);
        platform.fillRect(x, y, 3, h);
    }

    /**
     * Create moving platforms
     * @param {Object} config - Level configuration
     */
    createMovingPlatforms(config) {
        if (!config.movingPlatforms) return;

        config.movingPlatforms.forEach(p => {
            const body = this.scene.matter.add.rectangle(p.x + p.w / 2, p.y + p.h / 2, p.w, p.h, {
                isStatic: true,
                friction: 1.0
            });

            const visual = this.scene.add.rectangle(p.x + p.w / 2, p.y + p.h / 2, p.w, p.h, config.theme.platformColor);
            visual.setStrokeStyle(3, 0xffffff);

            const arrows = this.scene.add.text(p.x + p.w / 2, p.y + p.h / 2, '↔', {
                fontSize: '16px',
                fill: '#ffffff'
            }).setOrigin(0.5).setAlpha(0.5);

            const platform = { body, visual, arrows, w: p.w, h: p.h };
            platform.config = p;
            platform.startX = p.x + p.w / 2;
            platform.startY = p.y + p.h / 2;
            platform.time = 0;
            
            this.movingPlatforms.push(platform);
        });
    }

    /**
     * Create collectible stars
     * @param {Object} config - Level configuration
     */
    createStars(config) {
        config.stars.forEach(s => {
            const star = this.scene.matter.add.circle(s.x, s.y, 15, { 
                isStatic: true, 
                isSensor: true, 
                label: 'star' 
            });

            const glow = this.scene.add.circle(s.x, s.y, 30, 0xFFD700, 0.2);
            this.scene.tweens.add({
                targets: glow,
                scale: 1.4,
                alpha: 0.05,
                duration: 1000,
                yoyo: true,
                repeat: -1
            });

            const visual = this.scene.add.star(s.x, s.y, 5, 8, 16, 0xFFD700);
            visual.setStrokeStyle(2, 0xFFA500);
            star.gameObject = visual;

            this.scene.tweens.add({
                targets: visual,
                angle: 360,
                duration: 3000,
                repeat: -1
            });

            this.scene.tweens.add({
                targets: [visual, glow],
                y: s.y - 8,
                duration: 800,
                yoyo: true,
                repeat: -1,
                ease: 'Sine.easeInOut'
            });

            this.stars.push({ body: star, visual, glow });
        });
    }

    /**
     * Create puzzle elements (door, button, box)
     * @param {Object} config - Level configuration
     */
    createPuzzle(config) {
        // Door
        this.door = this.scene.matter.add.rectangle(
            config.door.x, config.door.y, config.door.w, config.door.h, 
            { isStatic: true, label: 'door' }
        );
        this.doorGraphic = this.scene.add.rectangle(
            config.door.x, config.door.y, config.door.w, config.door.h, 0x444455
        );
        this.doorGraphic.setStrokeStyle(2, 0x666677);

        // Door indicator
        const arrowY = config.door.y - config.door.h / 2 - 30;
        const arrow = this.scene.add.triangle(config.door.x, arrowY, 0, 20, 15, 0, 30, 20, 0x00ff00);
        this.scene.tweens.add({
            targets: arrow,
            y: arrowY - 10,
            duration: 500,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });

        // Button
        this.button = this.scene.matter.add.rectangle(
            config.button.x, config.button.y, 80, 15, 
            { isStatic: true, isSensor: true, label: 'button' }
        );

        const buttonBase = this.scene.add.rectangle(config.button.x, config.button.y + 5, 100, 10, 0x880088);
        const buttonVisual = this.scene.add.rectangle(config.button.x, config.button.y, 80, 15, 0xff00ff);
        buttonVisual.setStrokeStyle(2, 0xcc00cc);

        this.scene.tweens.add({
            targets: buttonVisual,
            alpha: 0.7,
            scaleX: 1.05,
            duration: 600,
            yoyo: true,
            repeat: -1
        });

        // Box
        const isFloating = config.box.floating === true;
        
        if (isFloating) {
            this.floatingBoxZone = this.createFloatingBoxZone(config.box.x, config.box.y);
        }

        const boxBody = this.scene.matter.add.rectangle(config.box.x, config.box.y, PHYSICS.BOX.SIZE, PHYSICS.BOX.SIZE, {
            friction: PHYSICS.BOX.FRICTION,
            frictionStatic: PHYSICS.BOX.FRICTION_STATIC,
            frictionAir: PHYSICS.BOX.FRICTION_AIR,
            restitution: PHYSICS.BOX.RESTITUTION,
            density: PHYSICS.BOX.DENSITY,
            label: isFloating ? 'magneticBox' : 'box',
            chamfer: { radius: PHYSICS.BOX.CHAMFER_RADIUS },
            isStatic: false
        });

        if (isFloating) {
            boxBody.ignoreGravity = true;
            this.scene.matter.body.setVelocity(boxBody, { x: 0, y: 0 });
            this.scene.matter.body.setAngularVelocity(boxBody, 0);
            this.magneticBox = boxBody;
            this.magneticBoxOriginalPosition = { x: config.box.x, y: config.box.y };
        }
        
        this.box = boxBody;

        const boxVisual = this.scene.add.rectangle(config.box.x, config.box.y, PHYSICS.BOX.SIZE, PHYSICS.BOX.SIZE, 
            isFloating ? 0x3366aa : 0x8B4513);
        boxVisual.setStrokeStyle(3, isFloating ? 0x00aaff : 0x654321);

        // Pattern
        const grain = this.scene.add.graphics();
        if (isFloating) {
            grain.lineStyle(1, 0x00aaff, 0.6);
            for (let i = -20; i < 20; i += 10) {
                grain.lineBetween(config.box.x - 20, config.box.y + i, config.box.x + 20, config.box.y + i);
            }
            grain.lineStyle(1, 0x00aaff, 0.4);
            grain.lineBetween(config.box.x, config.box.y - 20, config.box.x, config.box.y + 20);
        } else {
            grain.lineStyle(1, 0x5a3010, 0.5);
            for (let i = -20; i < 20; i += 8) {
                grain.lineBetween(config.box.x - 25, config.box.y + i, config.box.x + 25, config.box.y + i);
            }
        }

        this.trackedBodies.push({
            body: boxBody,
            visuals: [boxVisual, grain],
            offsetX: 0,
            offsetY: 0
        });

        // Box hint
        const hintText = isFloating ? '🧲 HOLD DOWN TO PULL' : '⬇ PUSH TO BUTTON';
        const boxIndicator = this.scene.add.text(config.box.x, config.box.y - 45, hintText, {
            fontSize: '12px',
            fontFamily: 'Arial Black',
            fill: isFloating ? '#00aaff' : '#00ffff',
            backgroundColor: '#00000088',
            padding: { x: 5, y: 3 }
        }).setOrigin(0.5);

        this.scene.tweens.add({
            targets: boxIndicator,
            alpha: 0,
            delay: 5000,
            duration: 1000,
            onComplete: () => boxIndicator.destroy()
        });
    }

    /**
     * Create magnetic box zone visual
     * @param {number} x - X position
     * @param {number} y - Y position
     * @returns {Object} Zone and text objects
     */
    createFloatingBoxZone(x, y) {
        const zoneSize = 100;
        const zone = this.scene.add.graphics();
        
        zone.lineStyle(3, 0x00aaff, 0.8);
        zone.strokeRect(x - zoneSize / 2, y - zoneSize / 2, zoneSize, zoneSize);
        
        // Corner markers
        const cornerSize = 10;
        zone.lineStyle(4, 0x00aaff, 1);
        zone.lineBetween(x - zoneSize / 2, y - zoneSize / 2, x - zoneSize / 2 + cornerSize, y - zoneSize / 2);
        zone.lineBetween(x - zoneSize / 2, y - zoneSize / 2, x - zoneSize / 2, y - zoneSize / 2 + cornerSize);
        zone.lineBetween(x + zoneSize / 2, y - zoneSize / 2, x + zoneSize / 2 - cornerSize, y - zoneSize / 2);
        zone.lineBetween(x + zoneSize / 2, y - zoneSize / 2, x + zoneSize / 2, y - zoneSize / 2 + cornerSize);
        zone.lineBetween(x - zoneSize / 2, y + zoneSize / 2, x - zoneSize / 2 + cornerSize, y + zoneSize / 2);
        zone.lineBetween(x - zoneSize / 2, y + zoneSize / 2, x - zoneSize / 2, y + zoneSize / 2 - cornerSize);
        zone.lineBetween(x + zoneSize / 2, y + zoneSize / 2, x + zoneSize / 2 - cornerSize, y + zoneSize / 2);
        zone.lineBetween(x + zoneSize / 2, y + zoneSize / 2, x + zoneSize / 2, y + zoneSize / 2 - cornerSize);

        this.scene.tweens.add({
            targets: zone,
            alpha: 0.4,
            duration: 1000,
            yoyo: true,
            repeat: -1
        });

        const zoneText = this.scene.add.text(x, y - zoneSize / 2 - 15, '🧲 MAGNETIC ZONE', {
            fontSize: '11px',
            fontFamily: 'Arial',
            fill: '#00aaff',
            backgroundColor: '#00000088',
            padding: { x: 6, y: 3 }
        }).setOrigin(0.5);

        return { zone, text: zoneText };
    }

    /**
     * Create goal flag
     * @param {Object} config - Level configuration
     */
    createGoal(config) {
        this.goal = this.scene.matter.add.rectangle(config.goal.x, config.goal.y, 50, 150, { 
            isStatic: true, 
            isSensor: true, 
            label: 'goal' 
        });

        const pole = this.scene.add.rectangle(config.goal.x, config.goal.y, 8, 150, 0xeeeeee);
        pole.setStrokeStyle(2, 0xcccccc);

        const flag = this.scene.add.polygon(config.goal.x + 30, config.goal.y - 50, [
            0, 0, 50, 15, 0, 30
        ], 0xff0000);
        flag.setStrokeStyle(2, 0xcc0000);

        this.scene.tweens.add({
            targets: flag,
            scaleX: 1.15,
            scaleY: 0.9,
            duration: 400,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });

        // Sparkle particles
        this.scene.time.addEvent({
            delay: 300,
            callback: () => {
                if (this.scene.scene.isActive()) {
                    this.createSparkle(
                        config.goal.x + Phaser.Math.Between(-30, 30), 
                        config.goal.y + Phaser.Math.Between(-70, 70)
                    );
                }
            },
            repeat: -1
        });

        this.goalText = this.scene.add.text(config.goal.x, config.goal.y - 100, '🏁 GOAL', {
            fontSize: '20px',
            fontFamily: 'Arial Black',
            fill: '#00ff00',
            stroke: '#000000',
            strokeThickness: 3
        }).setOrigin(0.5).setAlpha(0);
    }

    /**
     * Create sparkle effect
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    createSparkle(x, y) {
        const sparkle = this.scene.add.star(x, y, 4, 2, 6, 0xffff00);
        sparkle.setAlpha(0.8);
        this.scene.tweens.add({
            targets: sparkle,
            alpha: 0,
            scale: 2,
            duration: 500,
            onComplete: () => sparkle.destroy()
        });
    }

    /**
     * Add decorations (clouds, grass)
     * @param {Object} config - Level configuration
     */
    addDecorations(config) {
        // Clouds
        const cloudCount = Math.floor(config.worldWidth / 400);
        for (let i = 0; i < cloudCount; i++) {
            const x = Phaser.Math.Between(100, config.worldWidth - 100);
            const y = Phaser.Math.Between(50, 200);
            this.addCloud(x, y);
        }

        // Grass on platforms
        config.platforms.forEach(p => {
            if (p.h >= 40) {
                this.addGrass(p.x, p.y, p.w);
            }
        });

        // Custom image/SVG decorations
        if (Array.isArray(config.decorations)) {
            this.addImageDecorations(config.decorations);
        }
    }

    /**
     * Add image/SVG decorations from level config
     * Supports: single images, horizontal repeating, and centered placement
     * @param {Array} decorations - Decoration config array
     */
    addImageDecorations(decorations) {
        const worldWidth = this.scene.levelConfig?.worldWidth || 4000;
        const worldHeight = this.scene.levelConfig?.worldHeight || 1600;

        decorations.forEach((decor) => {
            // Check if this decoration should repeat horizontally
            if (decor.repeatX) {
                this.createRepeatingDecoration(decor, worldWidth, worldHeight);
            } else if (decor.centerInLevel) {
                // Center the decoration in the level
                this.createCenteredDecoration(decor, worldWidth, worldHeight);
            } else {
                // Single decoration at specified position
                this.createSingleDecoration(decor);
            }
        });
    }

    /**
     * Create a single decoration image
     * @param {Object} decor - Decoration config
     */
    createSingleDecoration(decor) {
        const image = this.scene.add.image(decor.x, decor.y, decor.key);
        this.applyDecorationProperties(image, decor);
    }

    /**
     * Create a centered decoration (optionally covering the entire level)
     * @param {Object} decor - Decoration config
     * @param {number} worldWidth - Level width
     * @param {number} worldHeight - Level height
     */
    createCenteredDecoration(decor, worldWidth, worldHeight) {
        const x = decor.x !== undefined ? decor.x : worldWidth / 2;
        const y = decor.y !== undefined ? decor.y : worldHeight / 2;
        
        const image = this.scene.add.image(x, y, decor.key);
        this.applyDecorationProperties(image, decor);
    }

    /**
     * Create horizontally repeating decorations across the level
     * @param {Object} decor - Decoration config
     * @param {number} worldWidth - Level width
     * @param {number} worldHeight - Level height
     */
    createRepeatingDecoration(decor, worldWidth, worldHeight) {
        // Get base image dimensions to calculate spacing
        const texture = this.scene.textures.get(decor.key);
        if (!texture || !texture.source[0]) {
            console.warn(`Texture not found: ${decor.key}`);
            return;
        }
        
        const baseWidth = texture.source[0].width;
        const scale = decor.scale || 1;
        const scaledWidth = baseWidth * scale;
        
        // Spacing between repeated images (can overlap or have gaps)
        const spacing = decor.spacing !== undefined ? decor.spacing : scaledWidth * 0.8;
        
        // Calculate how many decorations to create
        const count = Math.ceil(worldWidth / spacing) + 2;
        
        // Y position - default to middle-ish area
        const baseY = decor.y !== undefined ? decor.y : worldHeight * 0.4;
        
        for (let i = 0; i < count; i++) {
            const x = i * spacing;
            // Add slight Y variation if specified
            const yVariation = decor.yVariation || 0;
            const y = baseY + (yVariation ? Phaser.Math.Between(-yVariation, yVariation) : 0);
            
            const image = this.scene.add.image(x, y, decor.key);
            this.applyDecorationProperties(image, decor);
            
            // Optional: slight random rotation variation
            if (decor.rotationVariation) {
                const rotVar = decor.rotationVariation;
                image.setRotation((image.rotation || 0) + Phaser.Math.FloatBetween(-rotVar, rotVar));
            }
            
            // Optional: slight scale variation
            if (decor.scaleVariation) {
                const scaleVar = decor.scaleVariation;
                const currentScale = image.scale;
                image.setScale(currentScale * Phaser.Math.FloatBetween(1 - scaleVar, 1 + scaleVar));
            }
        }
    }

    /**
     * Apply common decoration properties to an image
     * @param {Phaser.GameObjects.Image} image - The image to configure
     * @param {Object} decor - Decoration config
     */
    applyDecorationProperties(image, decor) {
        image.setOrigin(0.5, 0.5);

        const texture = this.scene.textures.get(decor.key);
        if (texture?.setFilter) {
            texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
        }

        if (decor.scale !== undefined) {
            image.setScale(decor.scale);
        }
        if (decor.alpha !== undefined) {
            image.setAlpha(decor.alpha);
        }
        if (decor.rotation !== undefined) {
            image.setRotation(decor.rotation);
        }
        if (decor.depth !== undefined) {
            image.setDepth(decor.depth);
        } else {
            // Default depth behind gameplay elements
            image.setDepth(-50);
        }
        if (decor.scrollFactor !== undefined) {
            image.setScrollFactor(decor.scrollFactor);
        } else {
            // Default subtle parallax for background art
            image.setScrollFactor(0.2);
        }
        if (decor.tint !== undefined) {
            image.setTint(decor.tint);
        }
        if (decor.blendMode !== undefined) {
            image.setBlendMode(decor.blendMode);
        }
        if (decor.flipX) {
            image.setFlipX(true);
        }
        if (decor.flipY) {
            image.setFlipY(true);
        }
    }

    /**
     * Add grass to platform
     * @param {number} x - X position
     * @param {number} y - Y position
     * @param {number} width - Platform width
     */
    addGrass(x, y, width) {
        const grassGraphics = this.scene.add.graphics();
        grassGraphics.fillStyle(0x32CD32, 0.8);

        for (let i = 0; i < width; i += 15) {
            const grassX = x + i + Phaser.Math.Between(-3, 3);
            const grassHeight = Phaser.Math.Between(5, 12);

            grassGraphics.beginPath();
            grassGraphics.moveTo(grassX, y);
            grassGraphics.lineTo(grassX - 3, y - grassHeight);
            grassGraphics.lineTo(grassX + 3, y - grassHeight);
            grassGraphics.closePath();
            grassGraphics.fill();
        }
    }

    /**
     * Add cloud decoration
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    addCloud(x, y) {
        const cloud = this.scene.add.graphics();
        cloud.fillStyle(0xffffff, 0.6);
        cloud.fillCircle(0, 0, 30);
        cloud.fillCircle(25, -5, 25);
        cloud.fillCircle(50, 0, 30);
        cloud.fillCircle(25, 10, 20);
        cloud.x = x;
        cloud.y = y;
        cloud.setScrollFactor(0.2);

        this.scene.tweens.add({
            targets: cloud,
            x: x + 50,
            duration: Phaser.Math.Between(8000, 15000),
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut'
        });
    }

    /**
     * Create danger zone indicator
     * @param {Object} config - Level configuration
     */
    createDangerZone(config) {
        const fogGraphics = this.scene.add.graphics();
        const fogY = config.worldHeight - 100;

        for (let i = 0; i < 10; i++) {
            const alpha = 0.05 + (i * 0.02);
            fogGraphics.fillStyle(0xff0000, alpha);
            fogGraphics.fillRect(0, fogY + (i * 15), config.worldWidth, 20);
        }
    }

    /**
     * Update moving platforms
     * @param {number} delta - Delta time
     */
    updateMovingPlatforms(delta) {
        this.movingPlatforms.forEach(p => {
            if (!p || !p.body || !p.config) return;
            
            p.time += delta * p.config.speed;

            let newX = p.startX;
            let newY = p.startY;

            if (p.config.moveX) {
                newX = p.startX + Math.sin(p.time) * p.config.moveX;
            }
            if (p.config.moveY) {
                newY = p.startY + Math.sin(p.time) * p.config.moveY;
            }

            this.scene.matter.body.setPosition(p.body, { x: newX, y: newY });
            if (p.visual) {
                p.visual.x = newX;
                p.visual.y = newY;
            }
            if (p.arrows) {
                p.arrows.x = newX;
                p.arrows.y = newY;
            }
        });
    }

    /**
     * Get all tracked bodies for visual sync
     * @returns {Array}
     */
    getTrackedBodies() {
        return this.trackedBodies;
    }

    /**
     * Show goal text
     */
    showGoalText() {
        if (this.goalText) {
            this.scene.tweens.add({
                targets: this.goalText,
                alpha: 1,
                duration: 500
            });
        }
    }

    /**
     * Animate door opening
     * @param {Object} config - Level configuration
     */
    openDoor(config) {
        this.scene.tweens.addCounter({
            from: config.door.y,
            to: config.door.y - 350,
            duration: 1200,
            ease: 'Power2',
            onUpdate: (tween) => {
                const y = tween.getValue();
                this.scene.matter.body.setPosition(this.door, { x: config.door.x, y: y });
                this.doorGraphic.y = y;
            }
        });
    }
}
